import { NIL as EMPTY_GUID } from 'uuid';
import { Injectable } from '@angular/core';
import { orderBy } from 'lodash-es';
import moment from 'moment';
import { BehaviorSubject, concat, Observable, of, combineLatest } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CacheService, IEntityCacheService } from '@nimbus-library/features/cache';
import { FeatureReadModel } from '@nimbus-global/features/feature-flipping/feature.model';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { FeatureFlippingApiClient } from './feature-flipping.api-client';
import { Feature } from './feature.enum';

export type IFeaturesMap = { [feature in Feature]?: boolean };
export type IFeaturesSubjectMap = { [feature in Feature]?: BehaviorSubject<boolean> };

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class FeatureFlippingService {
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  private readonly CACHE_TIMEOUT = moment.duration(5, 'seconds');

  private readonly PRELOAD_TIMEOUT = moment.duration(1, 'months');

  private features$: BehaviorSubject<IFeaturesMap>;
  private featuresFlipping$: BehaviorSubject<IFeaturesSubjectMap>;
  // TODO: remove specific feature subjects and migrate to generic methods (isEnabledInFeatureManagementByTenantId$) and properties (featuresFlipping$)
  private isVirtualUserEnabledForCurrentTenant$ = new BehaviorSubject<boolean>(false);
  private isCompanionDefaultTranscriberEnabledForCurrentTenant$ = new BehaviorSubject<boolean>(false);
  private entityCache: IEntityCacheService<boolean>;
  private readonly fetchedFeatures = new Set<string>();

  public featuresLoaded$ = new BehaviorSubject(false);

  public constructor(
    private readonly authenticationService: IAuthenticationService,
    private readonly cacheService: CacheService,
    private readonly nimbusSignalRHub: NimbusSignalRHub,
    private readonly featureFlippingApiClient: FeatureFlippingApiClient,
  ) {
    this.initialize();
  }

  public get(): Observable<FeatureReadModel[]> {
    return this.featureFlippingApiClient.get().pipe(map((features) => orderBy(features, (x) => x.name)));
  }

  public areEnabled$(features: Feature[]): Observable<IFeaturesMap> {
    return concat(of(this.features$.value), this.features$).pipe(
      skipWhile((x) => features.some((f) => f in x && x[f] === undefined)),
      distinctUntilChanged(),
      map((x) => {
        const result = {};
        features.forEach((f) => (result[f] = x[f]));
        return result;
      }),
      distinctUntilChanged(),
    );
  }

  public areEnabled(features: Feature[], preload: boolean = false): Observable<IFeaturesMap> {
    return this.featureFlippingApiClient.areEnabled(features).pipe(
      this.entityCache.useMapCache(features, preload),
      tap((value) => {
        this.features$.next({ ...this.features$.value, ...value });
      }),
    );
  }

  public isEnabledInFeatureManagement(feature: Feature): Observable<boolean> {
    return this.authenticationService.isAuthenticated$.pipe(
      distinctUntilChanged(),
      filter(Boolean),
      take(1),
      switchMap(() => {
        const tenantId = this.authenticationService.getUserInfo()?.tenantId || EMPTY_GUID;
        return this.featureFlippingApiClient.isEnabledInFeatureManagement(tenantId, feature);
      }),
    );
  }

  // Feature Flipping per tenant methods

  public getIsFeatureFlippingEnabledInCurrentTenant$(feature: Feature): Observable<boolean> {
    this.ensureFeatureFlippingSubjectValue(feature);
    return this.featuresFlipping$.value[feature].asObservable();
  }

  public resetIsFeatureFlippingEnabledInCurrentTenant(feature: Feature): void {
    this.ensureFeatureFlippingSubjectValue(feature);
    this.featuresFlipping$.value[feature].next(false);
  }

  public resetFeaturesFlippingEnabledInCurrentTenant(features: Feature[]): void {
    if (!features?.length) {
      return;
    }
    features.forEach((f) => {
      this.ensureFeatureFlippingSubjectValue(f);
      this.featuresFlipping$.value[f].next(false);
    });
  }

  public getCurrentFeaturesFlippingSnapshot(features: Feature[]): IFeaturesMap {
    return (features || []).reduce((acc, f) => {
      this.ensureFeatureFlippingSubjectValue(f);
      acc[f] = this.featuresFlipping$.value[f].value;
      return acc;
    }, {} as IFeaturesMap);
  }

  public isEnabledInFeatureManagementByTenantId$(tenantId: string | null | undefined, feature: Feature): Observable<boolean> {
    this.ensureFeatureFlippingSubjectValue(feature);
    const featureSubject = this.featuresFlipping$.value[feature];

    if (!tenantId) {
      featureSubject.next(false);
      return of(false);
    }

    return this.featureFlippingApiClient.isEnabledInFeatureManagement(tenantId, feature).pipe(
      catchError(() => {
        featureSubject.next(false);
        return of(false);
      }),
      tap((enabled) => featureSubject.next(enabled)),
      tap(() => this.fetchedFeatures.add(this.getFeatureCacheKey(tenantId, feature))),
    );
  }

  /**
   * True when the feature is enabled for at least one of the supplied tenants, resolved in a single request.
   * A per-tenant fan-out here costs one gateway round trip per tenant and blocks the caller until all of them
   * settle — see bug #153286.
   */
  public areEnabledInFeatureManagementByTenantIds$(tenantIds: string[], feature: Feature): Observable<boolean> {
    return this.getTenantIdsWithFeatureEnabled$(tenantIds, feature).pipe(map((enabledTenantIds) => enabledTenantIds.length > 0));
  }

  public getTenantIdsWithFeatureEnabled$(tenantIds: string[], feature: Feature): Observable<string[]> {
    if (!tenantIds?.length) {
      return of([]);
    }

    return this.featureFlippingApiClient.getTenantsWithFeatureEnabled(feature, tenantIds).pipe(
      map((enabledTenantIds) => enabledTenantIds ?? []),
      catchError(() => of([])),
    );
  }

  public areEnabledInFeatureManagementByTenantId$(
    tenantId: string | null | undefined,
    features: Feature[],
    queryCachedFeatures: boolean = false,
  ): Observable<IFeaturesMap> {
    if (!features?.length) {
      return of({});
    }

    if (!tenantId) {
      features.forEach((f) => {
        this.ensureFeatureFlippingSubjectValue(f);
        this.featuresFlipping$.value[f].next(false);
      });
      return of(features.reduce((acc, f) => ({ ...acc, [f]: false }), {} as IFeaturesMap));
    }

    const perFeature$ = features.map((f) => {
      const featureKey = this.getFeatureCacheKey(tenantId, f);
      if (!queryCachedFeatures || !this.fetchedFeatures.has(featureKey)) {
        return this.isEnabledInFeatureManagementByTenantId$(tenantId, f).pipe(map((enabled) => [f, enabled] as [Feature, boolean]));
      }

      this.ensureFeatureFlippingSubjectValue(f);
      return this.featuresFlipping$.value[f].asObservable().pipe(map((enabled) => [f, enabled] as [Feature, boolean]));
    });

    return combineLatest(perFeature$).pipe(
      map((entries) =>
        entries.reduce((acc, [f, enabled]) => {
          acc[f] = enabled;
          return acc;
        }, {} as IFeaturesMap),
      ),
      distinctUntilChanged((a, b) => {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) {
          return false;
        }
        return aKeys.every((k) => a[k] === b[k]);
      }),
    );
  }

  public getIsVirtualUserEnabledInCurrentTenant$(): Observable<boolean> {
    return this.isVirtualUserEnabledForCurrentTenant$.asObservable();
  }

  public resetIsVirtualUserEnabledInCurrentTenant(): void {
    this.isVirtualUserEnabledForCurrentTenant$.next(false);
  }

  public isVirtualUserEnabledByTenantId$(tenantId: string | null | undefined): Observable<boolean> {
    if (!tenantId) {
      this.isVirtualUserEnabledForCurrentTenant$.next(false);
      return of(false);
    }

    return this.featureFlippingApiClient.isEnabledInFeatureManagement(tenantId, Feature.VirtualUser).pipe(
      catchError(() => {
        this.isVirtualUserEnabledForCurrentTenant$.next(false);
        return of(false);
      }),
      tap((enabled) => this.isVirtualUserEnabledForCurrentTenant$.next(enabled)),
    );
  }

  public getIsCompanionDefaultTranscriberEnabledInCurrentTenant$(): Observable<boolean> {
    return this.isCompanionDefaultTranscriberEnabledForCurrentTenant$.asObservable();
  }

  public resetIsCompanionDefaultTranscriberEnabledInCurrentTenant(): void {
    this.isCompanionDefaultTranscriberEnabledForCurrentTenant$.next(false);
  }

  public isCompanionDefaultTranscriberEnabledByTenantId$(tenantId: string | null | undefined): Observable<boolean> {
    if (!tenantId) {
      this.isCompanionDefaultTranscriberEnabledForCurrentTenant$.next(false);
      return of(false);
    }

    return this.featureFlippingApiClient.isEnabledInFeatureManagement(tenantId, Feature.CompanionDefaultTranscriber).pipe(
      catchError(() => {
        this.isCompanionDefaultTranscriberEnabledForCurrentTenant$.next(false);
        return of(false);
      }),
      tap((enabled) => this.isCompanionDefaultTranscriberEnabledForCurrentTenant$.next(enabled)),
    );
  }

  private ensureFeatureFlippingSubjectValue(feature: Feature): void {
    const featuresFlippingMap = this.featuresFlipping$.value;
    if (!featuresFlippingMap[feature]) {
      const updatedMap = { ...featuresFlippingMap, [feature]: new BehaviorSubject<boolean>(false) };
      this.featuresFlipping$.next(updatedMap);
    }
  }

  private initialize(): void {
    this.featuresFlipping$ = new BehaviorSubject<IFeaturesSubjectMap>({});
    this.features$ = new BehaviorSubject<IFeaturesMap>({});
    this.entityCache = this.cacheService.for<boolean>('Features', { cacheMaxAge: this.CACHE_TIMEOUT, preloadMaxAge: this.PRELOAD_TIMEOUT });

    this.authenticationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        filter((isAuthenticated) => isAuthenticated),
        switchMap(() =>
          this.nimbusSignalRHub.onFeaturesChanged$.pipe(
            distinctUntilChanged(),
            map((x) => this.nextFeatures(x)),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private nextFeatures(features: IFeaturesMap): void {
    if (!this.featuresLoaded$.value) {
      this.featuresLoaded$.next(true);
    }
    this.features$.next({ ...this.features$.value, ...features });
    if (window['debug']) {
      // eslint-disable-next-line
      console.log(`FeatureFlippingService. Features: ${JSON.stringify(features)}`);
    }
  }

  private getFeatureCacheKey(tenantId: string, feature: Feature): string {
    return `${tenantId}:${feature}`;
  }
}
